import React, { useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { SpeakerHigh, Waveform } from '@phosphor-icons/react';

const VOICE_DESCRIPTIONS = {
  alloy:   'Neutral, balanced',
  ash:     'Clear, articulate',
  coral:   'Warm, friendly',
  echo:    'Smooth, calm',
  fable:   'Expressive, storytelling',
  nova:    'Energetic, upbeat',
  onyx:    'Deep, authoritative',
  sage:    'Wise, measured',
  shimmer: 'Bright, cheerful',
};

const VOICES = Object.keys(VOICE_DESCRIPTIONS);

/**
 * Real bubble voice using OpenAI TTS. Owner picks the voice from 9 options,
 * any visitor can hear the bubble speak its catchphrase or arbitrary text.
 */
const BubbleVoice = ({ bubble, isOwner, onUpdate }) => {
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [audio, setAudio] = useState(null);

  const speak = async (text, voiceOverride) => {
    if (!text) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/bubbles/${bubble.id}/speak`, {
        text, voice: voiceOverride,
      });
      const src = `data:audio/mp3;base64,${data.audio_b64}`;
      const a = new Audio(src);
      setAudio(a);
      await a.play();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'voice failed');
    } finally { setBusy(false); }
  };

  const pickVoice = async (v) => {
    try {
      await api.post(`/bubbles/${bubble.id}/voice`, { voice: v });
      toast.success(`${bubble.name} now speaks in ${v}`);
      setPicking(false);
      onUpdate && onUpdate();
      // preview new voice
      speak(`Hi, I'm ${bubble.name}. ${bubble.personality?.catchphrase || ''}`, v);
    } catch {
      toast.error('could not save voice');
    }
  };

  const text = bubble.personality?.catchphrase || `Hello, I am ${bubble.name}.`;

  return (
    <>
      <button
        data-testid="bubble-voice-btn"
        disabled={busy}
        onClick={() => speak(text)}
        className="px-5 py-3 rounded-full glass-hi text-sm font-bold flex items-center gap-2 hover:bg-white/10 transition disabled:opacity-50"
      >
        <SpeakerHigh size={14} weight="fill" className="text-bx-cyan" />
        {busy ? `${bubble.name} is speaking…` : `Hear ${bubble.name}`}
        <span className="text-[10px] uppercase tracking-widest text-white/60">·{bubble.voice || 'nova'}</span>
      </button>

      {isOwner && (
        <button
          data-testid="bubble-voice-pick"
          onClick={() => setPicking((p) => !p)}
          className="px-3 py-3 rounded-full glass-hi text-sm font-bold hover:bg-white/10 transition"
        >
          <Waveform size={14} weight="bold" />
        </button>
      )}

      {picking && (
        <div className="w-full mt-3 glass-hi rounded-2xl p-4" data-testid="voice-picker">
          <div className="text-[10px] uppercase tracking-widest text-bx-cyan flex items-center gap-1 mb-3">
            <Waveform size={12} weight="fill" /> pick a natural voice
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {VOICES.map((v) => (
              <button
                key={v}
                data-testid={`voice-${v}`}
                onClick={() => pickVoice(v)}
                disabled={busy}
                className={`px-3 py-2 rounded-lg text-left transition border ${
                  bubble.voice === v
                    ? 'border-bx-cyan bg-bx-cyan/10'
                    : 'border-white/10 glass-hi hover:bg-white/5'
                }`}
              >
                <div className="text-sm font-bold capitalize">{v}</div>
                <div className="text-[10px] uppercase tracking-widest text-white/60">{VOICE_DESCRIPTIONS[v]}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default BubbleVoice;
